import { createSelector } from 'reselect';

import type { RootState } from 'config/store/reducers';

import { isAdvicePending, isAnsweredCheckInProactiveWork } from 'works/utils';
import { ADVICE_STATUS, CHECKLIST_TYPE, SEVERITY } from 'modules/entity/constants';

import { getIds, getAdvicesByIds, getOfferedAdvices, getAdvices } from '.';

const getRoot = (state: RootState) => state;

export const getDoneAdvicesIds = createSelector(getIds, ids => ids[CHECKLIST_TYPE.DONE] ?? []);

const getDoneAdvices = createSelector(getRoot, getDoneAdvicesIds, getAdvicesByIds);

const getWorkAdvicesIds = createSelector(getAdvices, (advices) =>
  advices
    .filter(({ isProactive, checklistType, answeredOrigin }) => checklistType === CHECKLIST_TYPE.WORK && (!isProactive || !answeredOrigin))
    .map(({ adviceId }) => adviceId));

const getReminderScheduledAdvicesIds = createSelector(
  getIds,
  ids => ids[CHECKLIST_TYPE.REMINDER_SCHEDULED] ?? []
);

const getUpsellingAdvicesIds = createSelector(getIds, ids => ids[CHECKLIST_TYPE.UPSELLING] ?? []);

const getCheckInProactiveAdvicesIds = createSelector(
  getAdvices,
  advices => advices.filter(isAnsweredCheckInProactiveWork).map(({ adviceId }) => adviceId)
);

export const getCheckInAdvicesIds = createSelector(
  getUpsellingAdvicesIds,
  getCheckInProactiveAdvicesIds,
  (upsellingIds, proactiveIds) => [...upsellingIds, ...proactiveIds]
);

const getWorkAndReminderAdvicesIds = createSelector(
  getWorkAdvicesIds,
  getReminderScheduledAdvicesIds,
  (workAdvices, reminderScheduledAdvices) => [...workAdvices, ...reminderScheduledAdvices]
);

const getWorksAdvicesData = createSelector(getRoot, getWorkAndReminderAdvicesIds, getAdvicesByIds);
const getWorksAdvices = createSelector(getWorksAdvicesData, advices =>
  advices.filter(({ severity }) => severity === SEVERITY.LOW || severity === SEVERITY.HIGH));

const getAnsweredWorkAdvices = createSelector(getWorksAdvices, advices =>
  advices.filter(({ status }) => status !== ADVICE_STATUS.PENDING));

const getAnsweredOfferedAdvices = createSelector(getOfferedAdvices, advices =>
  advices.filter(({ status }) => status !== ADVICE_STATUS.PENDING));

const getAnsweredAdvices = createSelector(
  getAnsweredWorkAdvices,
  getAnsweredOfferedAdvices,
  (workAdvices, offeredAdvices) => [...workAdvices, ...offeredAdvices]
);

export const getAnsweredAdvicesIds = createSelector(getAnsweredAdvices, advices =>
  advices.map(({ adviceId }) => adviceId));

const getPendingWorksAdvices = createSelector(getWorksAdvices, advices => advices.filter(isAdvicePending));

export const hasAcceptedWorks = createSelector(getWorksAdvices, works => works.some(work => work.accepted));

const getPendingWorksAdvicesIds = createSelector(getPendingWorksAdvices, advices =>
  advices.map(({ adviceId }) => adviceId));

const getPendingOfferedAdvices = createSelector(getOfferedAdvices, advices => advices.filter(isAdvicePending));

const getPendingOfferedAdvicesIds = createSelector(getPendingOfferedAdvices, advices =>
  advices.map(({ adviceId }) => adviceId));

export const getPendingAdvicesIds = createSelector(
  getPendingWorksAdvicesIds,
  getPendingOfferedAdvicesIds,
  (pendingIds, offeredIds) => [...pendingIds, ...offeredIds]
);

/**
 * Get additional advices from checklist type WORK and severity HIGH, LOW, or OFFERED. Offered advices are at the end of the list.
 */
export const getWorkAdvicesIdsWithOffered = createSelector(
  getAnsweredAdvicesIds,
  getPendingAdvicesIds,
  (answeredAdvices, pendingAdvices) => [...answeredAdvices, ...pendingAdvices]
);

const getWorkAdvicesWithOffered = createSelector(getRoot, getWorkAdvicesIdsWithOffered, getAdvicesByIds);

const getAllAdvicesIdsWithOffered = createSelector(
  getDoneAdvicesIds,
  getCheckInAdvicesIds,
  getWorkAdvicesIdsWithOffered,
  (...lists) => lists.flat()
);

const getAllAdvicesWithOffered = createSelector(getRoot, getAllAdvicesIdsWithOffered, getAdvicesByIds);

const getAllAdvicesIds = createSelector(
  getDoneAdvicesIds,
  getWorkAndReminderAdvicesIds,
  getCheckInAdvicesIds,
  (...lists) => lists.flat()
);

export const canShowNewAdvices = createSelector(getAllAdvicesWithOffered, advices =>
  advices.some(({ accepted }) => accepted));

export const hasDoneWorksToAccept = createSelector(getDoneAdvices, advices =>
  advices.some(a => a.status === ADVICE_STATUS.PENDING));

export const hasWorksToAccept = createSelector(getWorksAdvicesData, advices =>
  advices.some(a => a.status === ADVICE_STATUS.PENDING));

export const hasWorksToAcceptWithOffered = createSelector(getWorkAdvicesWithOffered, advices =>
  advices.some(a => a.status === ADVICE_STATUS.PENDING));

export const hasPendingAdvices = createSelector(
  hasDoneWorksToAccept,
  hasWorksToAccept,
  (hasDonePending, hasWorkPending) => hasDonePending || hasWorkPending
);

export const countNumberDisplayedWorks = createSelector(
  getWorkAdvicesIdsWithOffered,
  getDoneAdvicesIds,
  getCheckInAdvicesIds,
  (...lists) => lists.reduce((acc, list) => acc + (list?.length || 0), 0)
);

export const hasWorks = createSelector(getAllAdvicesIds, advices => advices.length > 0);

export const hasAnswered = createSelector(hasPendingAdvices, hasAdvices => !hasAdvices);

export const hasFreeAdvices = createSelector(getAdvices, advices => advices.some(({ priceWithoutDiscountWithVAT }) => priceWithoutDiscountWithVAT === 0));
